/**
 * FAQ, Pricing and the demo form in Or's design.
 *
 * Three small components rather than three files: they share the rail, the
 * heading rhythm and the card, and splitting them would scatter one idea.
 *
 * Copy is the approved 30 September set throughout — the mockup's own FAQ
 * and pricing text is placeholder (its pricing page shows no figure at all,
 * where Q12 settled on $99/month).
 */

import React from 'react';
import CountedTextarea from '../CountedTextarea';
import { copyFor, DEMO_FORM, PRICING, SHARED, SHARED_FAQ } from '../audienceCopy';

/**
 * The mockup's accordion — a native disclosure, no JavaScript.
 *
 * It closes on a ghost "← Back", which the mockup puts at the foot of both
 * prose pages. It is the only way out of them: Or's header offers FAQ,
 * Pricing and the demo, but nothing that returns to the journey.
 */
export function MockFaq({ audience, onRequestDemo, onBack }) {
  const copy = copyFor(audience);
  const entries = [...copy.faq, ...SHARED_FAQ];

  return (
    <>
      <p className="mk-eyebrow">FAQ</p>
      <h1 className="mk-h1">{copy.faqIntro}</h1>

      <div className="mk-faq">
        {entries.map((entry, i) => (
          // The first is open, as in the mockup: an accordion where
          // everything is shut reads as an empty page.
          <details key={entry.question} open={i === 0}>
            <summary>{entry.question}</summary>
            {entry.answer.split('\n\n').map((para) => (
              <p className="mk-faq-answer" key={para.slice(0, 40)}>{para}</p>
            ))}
            {entry.demoCta && onRequestDemo && (
              <div className="mk-actions">
                <button type="button" className="mk-btn mk-btn--ghost" onClick={onRequestDemo}>
                  {SHARED.requestAGuidedDemo}
                </button>
              </div>
            )}
          </details>
        ))}
      </div>

      <MockBack onBack={onBack} />
    </>
  );
}

/** The mockup's only exit from a prose page. */
function MockBack({ onBack }) {
  if (!onBack) return null;
  return (
    <div className="mk-actions mk-actions--start">
      <button type="button" className="mk-btn mk-btn--ghost" onClick={onBack}>← Back</button>
    </div>
  );
}

export function MockPricing({ audience, onRequestDemo, onUpgrade, onBack }) {
  const copy = copyFor(audience);
  return (
    <>
      <p className="mk-eyebrow">Pricing</p>
      <h1 className="mk-h1">{copy.pricingIntro}</h1>
      <p className="mk-lede">{PRICING.lede}</p>

      <div className="mk-card">
        <p className="mk-eyebrow">After the first 14 days</p>
        <p className="mk-price">{PRICING.price}</p>
        <p className="mk-body">{PRICING.priceBody}</p>
        {/* Q12 — the destination is deferred, so the button is absent
            rather than dead until there is somewhere to send people. */}
        {onUpgrade && (
          <div className="mk-actions">
            <button type="button" className="mk-btn mk-btn--primary" onClick={onUpgrade}>
              {SHARED.upgradeAction}
            </button>
          </div>
        )}
      </div>

      <div className="mk-card">
        <h2 className="mk-h2">{PRICING.platformTitle}</h2>
        <p className="mk-body">{PRICING.platformBody}</p>
        <div className="mk-actions">
          <button
            type="button"
            className={`mk-btn ${onUpgrade ? 'mk-btn--ghost' : 'mk-btn--primary'}`}
            onClick={onRequestDemo}
          >
            {SHARED.requestAGuidedDemo}
          </button>
        </div>
      </div>

      <MockBack onBack={onBack} />
    </>
  );
}

/**
 * The demo request. Drop-in for `DemoRequestForm`: same props, same
 * self-contained state, same rules — a failed submission keeps what was
 * typed (§4), and nothing promises a response time.
 */
export function MockDemo({ prefill = {}, onSubmit, onBack, submitting, error }) {
  const nameRef = React.useRef(null);
  const [name, setName] = React.useState(prefill.name || '');
  const [email, setEmail] = React.useState(prefill.email || '');
  const [organization, setOrganization] = React.useState(prefill.organization || '');
  const [interest, setInterest] = React.useState('');
  const [sent, setSent] = React.useState(false);

  const valid = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && organization.trim();

  // The caret waits in the first field: this route is reached by a direct
  // link as often as by a button, so there is nothing else on the page to
  // have been interacting with.
  React.useEffect(() => { nameRef.current?.focus(); }, []);

  const submit = async () => {
    if (!valid || submitting) return;
    try {
      await onSubmit({
        name: name.trim(), email: email.trim(), organization: organization.trim(), interest: interest.trim(),
      });
      setSent(true);
    } catch {
      /* The page shows the error; the entered values stay put. */
    }
  };

  if (sent) {
    return (
      <>
        <p className="mk-eyebrow">Guided demo</p>
        <h1 className="mk-h1 mk-h1--sm">{DEMO_FORM.confirmation}</h1>
        <MockBackCentered onBack={onBack} />
      </>
    );
  }

  const field = (id, label, value, set, extra = {}) => (
    <div className="mk-field">
      <label className="mk-label" htmlFor={id}>{label}</label>
      <input id={id} className="mk-input" value={value} onChange={(e) => set(e.target.value)} {...extra} />
    </div>
  );

  return (
    <>
      <p className="mk-eyebrow">Guided demo</p>
      <h1 className="mk-h1">{DEMO_FORM.title}</h1>
      <p className="mk-lede">{DEMO_FORM.lede}</p>

      <div className="mk-card mk-form">
        {field('mk-demo-name', DEMO_FORM.name, name, setName, { ref: nameRef })}
        {field('mk-demo-email', DEMO_FORM.email, email, setEmail, { type: 'email' })}
        {field('mk-demo-org', DEMO_FORM.organization, organization, setOrganization)}
        <div className="mk-field">
          <label className="mk-label" htmlFor="mk-demo-interest">
            {DEMO_FORM.interest}{' '}
            <span style={{ fontWeight: 400, color: 'var(--mk-muted)' }}>Optional</span>
          </label>
          <CountedTextarea
            id="mk-demo-interest"
            wrapClassName="mk-textarea-wrap"
            className="mk-textarea"
            counterClassName="mk-counter"
            style={{ minHeight: 110 }}
            value={interest}
            onChange={setInterest}
          />
        </div>

        {error && <div className="mk-error">{error}</div>}

        {/* The mockup keeps the card to one action and puts the way out
            underneath it — see MockBackCentered. */}
        <div className="mk-actions">
          <button type="button" className="mk-btn mk-btn--primary" onClick={submit} disabled={submitting || !valid}>
            {SHARED.requestAGuidedDemo}
          </button>
        </div>
      </div>

      <MockBackCentered onBack={onBack} />
    </>
  );
}

/** The demo page's exit: below the card, centred, as the mockup has it. */
function MockBackCentered({ onBack }) {
  if (!onBack) return null;
  return (
    <div className="mk-actions mk-actions--center">
      <button type="button" className="mk-btn mk-btn--ghost" onClick={onBack}>← Back</button>
    </div>
  );
}
